/**
 * Shared form validation for generic DocType detail pages.
 *
 * Validates all child tables in a document against their schemas,
 * checking required fields and Link field references.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { DocTypeSchema } from "@/lib/erp/meta";
import { erp } from "@/lib/erp";
import { validateChildTable } from "@/components/child-table/utils/validation";
import { validateRowLinks } from "@/components/child-table/utils/linkValidation";
import { getVisibleChildTableFields } from "@/components/child-table/utils/schemaUtils";

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate a document's child tables against their schemas.
 * Checks required fields and Link field references for every child table row.
 */
export async function validateDocForm(
  data: Record<string, any>,
  metaSchema: DocTypeSchema,
  queryClient: QueryClient
): Promise<ValidationResult> {
  const errors: string[] = [];

  // Find all Table fields from metadata
  const tableFields = metaSchema.fields.filter(
    (field) => field.fieldtype === "Table" && field.options
  );

  // Validate each child table
  for (const field of tableFields) {
    const tableData = data[field.fieldname];

    if (Array.isArray(tableData) && tableData.length > 0) {
      // Get child schema (use cache if available)
      let childSchema: DocTypeSchema | null | undefined =
        queryClient.getQueryData<DocTypeSchema>(["meta", field.options]);

      if (!childSchema) {
        try {
          childSchema = await queryClient.fetchQuery<DocTypeSchema>({
            queryKey: ["meta", field.options],
            queryFn: () => erp.meta.getDocTypeSchema(field.options!),
            retry: false,
          });
        } catch (schemaError) {
          const errorMsg =
            schemaError instanceof Error
              ? schemaError.message
              : String(schemaError);
          const isPermissionError = /403|forbidden|permission/i.test(errorMsg);

          errors.push(
            `Cannot validate ${field.label || field.fieldname} child table: ${
              isPermissionError
                ? "Permission denied. Ensure your user can read metadata."
                : `Failed to fetch schema: ${errorMsg}`
            }`
          );
          continue;
        }
      }

      if (
        !childSchema ||
        !childSchema.fields ||
        childSchema.fields.length === 0
      ) {
        errors.push(
          `Cannot validate ${field.label || field.fieldname} child table: Schema has no fields available.`
        );
        continue;
      }

      // Get visible fields
      const visibleFields = getVisibleChildTableFields(childSchema);

      // Required field validation
      const requiredErrors = validateChildTable(
        tableData,
        visibleFields,
        field.label
      );
      errors.push(...requiredErrors.map((e) => e.message));

      // Link validation
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].__deleted) continue;
        const linkErrors = await validateRowLinks(
          tableData[i],
          i,
          visibleFields
        );
        errors.push(...linkErrors.map((e) => e.message));
      }
    }
  }

  return { valid: errors.length === 0, errors };
}
